import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { DisplayNameModel } from '@/models/core'

export type FormState = {
  /**
   * Geänderte Felder in der Form, Cards etc.
   *
   * Die geänderten Felder der Haupt-Form einer Ansicht werden unter _mainform['item] abgelegt,
   * um für alle Use-Cases die gleiche Struktur zu bewahren
   */
  changedListItems: Record<string, Record<string|number, { object: any, fields: string[] }>>
  /** Custom-Änderung die nicht in Feldern oder ListenItems darstellbar ist */
  changedCustom: boolean
  /** Form-Elemente die noch geladen werden  */
  loadingItems: string[]

  unsavedChangesWarningVisible: boolean
  documentUploadInProgress: boolean
  documentUploadInProgressDialogVisible: boolean
  /**
   * Darf der unsavedChangesDialog abgebrochen werden?
   * z.B. nach dem Löschen eines Objekts in einer FormCard **muss** refreshed werden
   * @default true
   **/
  canCancel: boolean
  /**
   * Darf der unsavedChangesDialog Änderungen ignorieren?
   * @default true
   */
  canIgnore: boolean

  /** uniqueCardNames von den FormCards, in denen gerade Items selected sind*/
  selectedFormCards: string[]
}


export const useFormStore = defineStore('form', () => {
  // State
  const changedListItems = ref<Record<string, Record<string|number, { object: any, fields: string[] }>>>({
    _mainform: { item: { object: null, fields: [] } }
  })
  const changedCustom = ref(false)
  const unsavedChangesWarningVisible = ref(false)
  const documentUploadInProgress = ref(false)
  const documentUploadInProgressDialogVisible = ref(false)
  const loadingItems = ref<string[]>([])
  const canCancel = ref(true)
  const canIgnore = ref(true)
  const selectedFormCards = ref<string[]>([])

  // Actions
  function addChangedFormField(field: string) {
    if (!changedListItems.value._mainform.item.fields.includes(field)) {
      changedListItems.value._mainform.item.fields.push(field)
    }
  }

  function removeChangedFormField(field: string) {
    changedListItems.value._mainform.item.fields = changedListItems.value._mainform.item.fields.filter(item => item !== field)
  }

  function clearChangedFormFields() {
    changedListItems.value['_mainform'] = { item: { object: null, fields: [] } }
  }

  function addChangedListItemField(view: string, object: DisplayNameModel, field: string) {
    if (!changedListItems.value[view]) {
      changedListItems.value[view] = {}
    }
    if (!changedListItems.value[view][object.id]) {
      changedListItems.value[view][object.id] = { object: object, fields: [] }
    }
    changedListItems.value[view][object.id].object = object
    if (!changedListItems.value[view][object.id].fields.includes(field)) {
      changedListItems.value[view][object.id].fields.push(field)
    }
  }

  function removeChangedListItemField(view: string, id: string, field: string) {
    if (changedListItems.value[view]?.[id]) {
      changedListItems.value[view][id].fields = changedListItems.value[view][id].fields.filter(item => item !== field)
    }
  }

  function removeChangedListItem(view: string, id: string|number) {
    if (changedListItems.value[view]) {
      delete changedListItems.value[view][id]
    }
  }

  function removeChangedView(view: string) {
    delete changedListItems.value[view]
  }

  // Hat eine bestimmte View (z.B. eine Liste/ein editierbarer Gantt) ungespeicherte geänderte Items?
  function isViewDirty(view: string): boolean {
    return Object.keys(changedListItems.value[view] ?? {}).length > 0
  }

  function clearChangedListItems() {
    for (const view of Object.keys(changedListItems.value)) {
      if (view !== '_mainform') {
        delete changedListItems.value[view]
      }
    }
  }

  function setChangedCustom(value: boolean) {
    changedCustom.value = value
  }

  function setDocumentUploadInProgress(payload: boolean) {
    documentUploadInProgress.value = payload
  }

  function showDocumentUploadInProgressDialog() {
    documentUploadInProgressDialogVisible.value = true
  }

  function hideDocumentUploadInProgressDialog() {
    documentUploadInProgressDialogVisible.value = false
  }

  function addLoadingItem(item: string) {
    if (!loadingItems.value.includes(item)) {
      loadingItems.value.push(item)
    }
  }

  function removeLoadingItem(item: string) {
    loadingItems.value = loadingItems.value.filter(i => i !== item)
  }

  function setCanCancel(value: boolean) {
    canCancel.value = value
  }

  function setCanIgnore(value: boolean) {
    canIgnore.value = value
  }

  function clearAll() {
    Object.keys(changedListItems.value).forEach(key => { delete changedListItems.value[key] })
    changedListItems.value._mainform = { item: { object: null, fields: [] } }
    changedCustom.value = false
    loadingItems.value = []
    unsavedChangesWarningVisible.value = false
    documentUploadInProgress.value = false
    documentUploadInProgressDialogVisible.value = false
    canCancel.value = true
    canIgnore.value = true
  }

  function showUnsavedWarning() {
    unsavedChangesWarningVisible.value = true
  }

  function hideUnsavedWarning() {
    unsavedChangesWarningVisible.value = false
  }

  function addSelectedFormCard(payload: string) {
    const index = selectedFormCards.value.indexOf(payload)
    if (index == -1) selectedFormCards.value.push(payload)
  }

  function removeSelectedFormCard(payload: string) {
    const index = selectedFormCards.value.indexOf(payload)
    if (index !== -1) selectedFormCards.value.splice(index, 1)
  }

  function clearSelectedFormCards() {
    selectedFormCards.value = []
  }

  // Getter
  const hasChangedData = computed((): boolean => {
    if (changedCustom.value)
      return true
    for (const view of Object.keys(changedListItems.value)) {
      if (view === '_mainform') {
        continue
      }
      if (Object.keys(changedListItems.value[view]).length > 0) {
        //View hat geänderte Items
        return true
      }
    }
    //Wurden Felder in der Haupt-Form geändert?
    return changedListItems.value._mainform.item.fields.length > 0
  })

  const isLoading = computed((): boolean => loadingItems.value.length > 0)

  const getChangedFormFields = computed((): string[] => changedListItems.value._mainform.item.fields.slice()) //kein pass-by-reference

  const getChangedListItems = computed((): any[] => {
    const ret: any[] = []
    for(const view of Object.keys(changedListItems.value)) {
      if(view !== '_mainform') {
        for(const id of Object.keys(changedListItems.value[view])) {
          ret.push({
            view: view,
            object: Object.assign({}, changedListItems.value[view][id].object), //kein pass-by-reference
            fields: changedListItems.value[view][id].fields.slice(), //kein pass-by-reference
          })
        }
      }
    }
    return ret
  })

  const getChangedListItemFields = computed(() => (view: string, id: string|number): string[] => {
    return changedListItems.value[view]?.[id || 0]?.fields || []
  })

  const isDocumentUploadInProgress = computed((): boolean => documentUploadInProgress.value)

  return {
    changedListItems,
    changedCustom,
    unsavedChangesWarningVisible,
    documentUploadInProgress,
    documentUploadInProgressDialogVisible,
    loadingItems,
    canCancel,
    canIgnore,
    selectedFormCards,
    addChangedFormField,
    removeChangedFormField,
    clearChangedFormFields,
    addChangedListItemField,
    removeChangedListItemField,
    removeChangedListItem,
    removeChangedView,
    isViewDirty,
    clearChangedListItems,
    setChangedCustom,
    setDocumentUploadInProgress,
    showDocumentUploadInProgressDialog,
    hideDocumentUploadInProgressDialog,
    addLoadingItem,
    removeLoadingItem,
    setCanCancel,
    setCanIgnore,
    clearAll,
    showUnsavedWarning,
    hideUnsavedWarning,
    addSelectedFormCard,
    removeSelectedFormCard,
    clearSelectedFormCards,
    hasChangedData,
    isLoading,
    getChangedFormFields,
    getChangedListItems,
    getChangedListItemFields,
    isDocumentUploadInProgress
  }
})